import React from 'react';
import PropTypes from 'prop-types';
import DraggableGrid from './grid/DraggableGrid';
import debounce from 'lodash.debounce';

// Components
import ChairIcon from 'dibs-vg/dist/react/chair-outlined';
import Slider from '../Toolbar/Slider';
import ZoomIcon from 'dibs-vg/dist/react/image';

import classnames from 'classnames';

// Styles
import styles from './styles/Tile.scss';

export const DraggableItem = ({ item }) => {
    const imageClasses = classnames(styles.draggableImage, {
        [styles.fullHeight]: !!item.title,
    });

    const dataTn = `${item.title ? 'folder' : 'item'}-draggable-container-${item.sort}`;

    return (
        <div className={styles.draggableImages} data-tn={dataTn}>
            <div className={styles.imageContainer}>
                {item.src ? (
                    <img className={imageClasses} src={item.src} />
                ) : (
                    <ChairIcon className={`${imageClasses} ${styles.chair}`} />
                )}
            </div>
            {item.title ? (
                <div className={styles.titleContainer}>
                    <div className={`${styles.title} ${styles.drag}`}>{item.title}</div>
                </div>
            ) : null}
        </div>
    );
};

DraggableItem.propTypes = {
    item: PropTypes.shape({
        src: PropTypes.string,
        sort: PropTypes.number,
        title: PropTypes.string,
    }),
};

class DraggableTiles extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            animate: true,
            items: props.draggableItems,
            moving: false,
            zoom: 1,
        };

        this.adjustZoom = this.adjustZoom.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onZoom = this.onZoom.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const completedReorder = !nextProps.reordered && this.props.reordered;
        this.setState({ animate: !completedReorder });

        if (!nextProps.reordered) {
            this.setState({
                items: nextProps.draggableItems,
            });
        }
    }

    shouldComponentUpdate(nextProps) {
        return !nextProps.reordered;
    }

    onDragEnd(draggedFromIndex, draggedToIndex) {
        if (this.state.moving) {
            this.setState({ moving: false });
        }
        this.props.onDrop(draggedFromIndex, draggedToIndex);
    }

    onDragStart() {
        if (!this.state.moving) {
            this.setState({ moving: true, animate: true });
        }
    }

    onMove(sourceIndex, targetIndex) {
        let source;
        let target;

        this.state.items.forEach(item => {
            if (item.marker === parseInt(sourceIndex, 10)) {
                source = item;
            }
            if (item.marker === parseInt(targetIndex, 10)) {
                target = item;
            }
        });

        const targetSort = target.sort;

        const items = this.state.items.map(item => {
            // If current item's marker is the moved item's marker (source), set that items sort order
            // to be equal to the target item's sort order (the index of the item that the item was moved to)
            if (item.marker === source.marker) {
                return {
                    ...item,
                    sort: targetSort,
                };
            } else if (
                target.sort > source.sort &&
                (item.sort <= target.sort && item.sort > source.sort)
            ) {
                // If the current item is in a position greater than the moved item's target location but less
                // than the moved item's original location (source), and the moved item
                // was moved in the positive direction, then decrease the current item's sort order by 1
                return {
                    ...item,
                    sort: item.sort - 1,
                };
            } else if (item.sort >= target.sort && item.sort < source.sort) {
                // If the current item sort position is greater than the moved item's target location but less
                // than the moved item's original location (source), and the moved item
                // was moved in the negative direction, then increase the current item's sort order by 1
                return {
                    ...item,
                    sort: item.sort + 1,
                };
            }
            // If the current item is not in between the moved item's sort and target positions, return that item as is
            return item;
        });

        this.setState({ items, moving: true });
    }

    onZoom(zoom) {
        this.setState({ zoom: zoom, animate: true });
    }

    adjustZoom(direction) {
        if (direction === 'decrease' && this.state.zoom > 0.5) {
            this.onZoom(this.state.zoom - 0.1);
        } else if (direction === 'increase' && this.state.zoom < 1.7) {
            this.onZoom(this.state.zoom + 0.1);
        }
    }

    render() {
        const animation = this.state.animate ? this.props.movingAnimationTransform : '';
        const onMoveDebounced = debounce(this.onMove, 80);

        return (
            <div className={styles.draggableContainerWrap}>
                <div className={styles.zoom}>
                    <div className={styles.minusIcon} onClick={() => this.adjustZoom('decrease')}>
                        <ZoomIcon className={styles.zoomIcon} />
                    </div>
                    <Slider
                        defaultCircleXOffset={(this.state.zoom - 0.5) / 1.2}
                        onSlide={zoom => this.onZoom(zoom)}
                    />
                    <div className={styles.plusIcon} onClick={() => this.adjustZoom('increase')}>
                        <ZoomIcon className={styles.zoomIcon} />
                    </div>
                </div>
                <div className={styles.draggableContainer}>
                    <DraggableGrid
                        animation={animation}
                        displayObject={DraggableItem}
                        dragEnabled
                        itemHeight={260}
                        items={this.state.items}
                        itemWidth={260}
                        cursorClass={this.state.moving ? styles.grabHand : styles.pointerHand}
                        moving={this.state.moving}
                        onDragStart={this.onDragStart}
                        onMove={onMoveDebounced}
                        onDragEnd={(draggedId, draggedToId) =>
                            this.onDragEnd(draggedId, draggedToId)
                        }
                        responsive
                        verticalMargin={36}
                        zoom={this.state.zoom}
                    />
                </div>
            </div>
        );
    }
}

DraggableTiles.defaultProps = {
    movingAnimationTransform: 'transform 300ms ease',
};

DraggableTiles.propTypes = {
    draggableItems: PropTypes.array,
    movingAnimationTransform: PropTypes.string,
    onDrop: PropTypes.func,
    reordered: PropTypes.bool,
};

export default DraggableTiles;
